import Cookies from 'universal-cookie';
import idx from 'idx';
import { buttonStateMap, validationTextMap, informationTextMap } from './constant';

const cookies = new Cookies();

export const isDisabled = state =>
  state === buttonStateMap.DISABLED || state === buttonStateMap.LOADING || state === buttonStateMap.TOC_NOT_CHECKED;

export const escapeScriptTags = html =>
  html.replace(
    /<script(?:(?!\/\/)(?!\/\*)[^'"]|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\/\/.*(?:\n)|\/\*(?:(?:.|\s))*?\*\/)*?<\/script>/g,
    ''
  );

/**
 * isCheckOK
 *
 * @param {object} res - response from Check API (api.oubo.rakuten.co.jp/1.0/entry/check)
 *    res.code:
 *      100: Maintenance
 *      200: OK
 *      201: Not allow (no Rz cookie)
 *      207: Invalid parameter
 *      300: Server error
 *    res.results.result:
 *      1 => already entry, 0 => not yet
 *
 * @link [Check API](https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=318769241)
 */
export const isCheckOK = res => +res.code === 200 && +idx(res, _ => _.results[0].result) === 0;

/**
 * isLoggedIn
 *
 * @param {object} res - response from Check API (api.oubo.rakuten.co.jp/1.0/entry/check)
 *
 * @link [Check API](https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=318769241)
 */
export const getIsLoggedIn = res => +res.code === 200;

/**
 * getStateByResultCode
 * returns buttonState
 *
 * @param {string} code - Result code from Check/Entry API
 *  Check API:
 *    0: Not yet applied
 *    1: Already applied
 *    'not found campaign': Campaign not found (cId is very liable to be wrong)
 *  Entry API:
 *    0: complete entry
 *    1: already entered
 *    2: out side of entry period
 *    3: can not enter due to not allowed device
 *    'not found campaign': Campaign not found (cId is very liable to be wrong)
 * @param {boolean} isCheckAPI - Whether the result code is from Check API or not (Entry API)
 *
 * @link [Check API](https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=318769241)
 * @link [Entry API](https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=679745995)
 */
export const getStateByResultCode = (code, isCheckAPI) => {
  switch (+code) {
    case 0: {
      /*
       * code === 0 means "good to go" in check API
       * and it also means "applied successfully" in apply API
       */
      return isCheckAPI ? buttonStateMap.ENABLED : buttonStateMap.SUCCESS;
    }
    case 1: {
      return buttonStateMap.ALREADY_APPLIED;
    }
    case 2: {
      return buttonStateMap.EXPIRED;
    }
    case 3:
    default: {
      return buttonStateMap.RETRY;
    }
  }
};

/**
 * buttonStateFactory
 * returns buttonState
 *
 * @param {object} res - response from Check OR Entry API
 *
 * @link [Check API](https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=318769241)
 * @link [Entry API](https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=679745995)
 */
export const buttonStateFactory = res => {
  // const isCheckAPI = idx(res, _ => _.results.length); // MEMO: this code will cause error on IE11
  const isCheckAPI = res && res.results && res.results.length;
  const resultCode = isCheckAPI ? res.results[0].result : res.result;

  // In case of res.code !== 200
  //  100, 201, 207, 300
  if (+res.code !== 200) {
    // MEMO: from 3.2.0, button will be 'ENABLED' when res.code === 201 (means not logged in).
    // return +res.code === 201 ? buttonStateMap.LOGIN : buttonStateMap.RETRY;
    return +res.code === 201 ? buttonStateMap.ENABLED : buttonStateMap.RETRY;
  }

  // In case of res.code === 200,
  // evaluate resultCode
  return getStateByResultCode(resultCode, isCheckAPI);
};

/**
 * validationTextFactory
 * returns validation text
 *
 * @param {object} res - response from Check OR Entry API
 */
export const validationTextFactory = res => {
  /*
   * result is an Array in check API
   * but a string in apply API
   */
  const resultCode = res.results && res.results.length ? res.results[0].result : res.result;

  if (+res.code === 200) {
    if (resultCode === 'not found campaign') {
      return validationTextMap.CAMPAIGN_NOT_FOUND;
    }

    /*
     * either the campaign has ended or the user has already applied before
     * it's meanlingless for us to display the statements or validation text anymore
     */
    switch (+resultCode) {
      case 0: // successful
      case 1: // already joined
      case 2: {
        // campaign expired
        // gonna show the feedback on the button instead of validation text
        return '';
      }
      case 3: {
        // device not allowed
        return validationTextMap.DEVICE_NOT_ALLOWED;
      }
      default:
        break;
    }
  } else {
    /*
     * we should properly tell user the reason they failed
     * with necessary information
     * and encourage them to retry
     */
    if (+res.code === 201) {
      // MEMO: 201 state returns,
      //  <= 3.1.1: LOGIN_ISSUE
      //  >= 3.2.0: blank string
      return '';
    } else if (+res.code === 100) {
      return validationTextMap.MAINTENANCE;
    }

    return validationTextMap.FALLBACK;
  }

  return '';
};

/**
 * informationTextFactory
 * returns information text
 *
 * @param {object} res - response from Check OR Entry API
 */
export const informationTextFactory = res => {
  if (+res.code === 201) {
    return informationTextMap.NOT_LOGGED_IN;
  }

  return '';
};

/**
 * sendInitialRAT
 *
 * @link https://confluence.rakuten-it.com/confluence/display/~takahiro.amakasu/Entry+Button+v2
 */
const RAT_ACCOUNT_ID_FOR_REX = '1253';
export const sendInitialRAT = (cId, mountId, ratAccountIdForCampaign) => {
  if (typeof window !== 'undefined' && window.RAT && typeof window.RAT.addCustomEvent === 'function') {
    const ratConfig = {
      accountId: RAT_ACCOUNT_ID_FOR_REX,
      serviceId: 1,
      includeInput: true,
      options: ['url', 'ua'],
      pData: {
        compid: [mountId || 'campaign_entry'],
      },
      cpData: {
        campaign_acc: ratAccountIdForCampaign,
        campaign_id: cId,
        campaign_solution: 'entry_button',
      },
      eventType: 'async',
    };

    try {
      RAT.addCustomEvent(ratConfig);
    } catch (e) {
      console.error(e);
    }
  }
};

export const sendRAT = (cId, success, mountId) => {
  if (typeof window !== 'undefined' && window.RAT && typeof window.RAT.addCustomEvent === 'function') {
    /*
     * [NOTE]
     *
     * if you wanna send the RAT data to test center
     * please manually set the a cookie: 'is-rat-test' to 1
     */
    const isRATTest = cookies.get('is-rat-test');

    const ratConfig = {
      // accountId: '484',
      serviceId: 1,
      includeInput: true,
      options: ['url', 'ua'],
      pData: {
        compid: [mountId || 'campaign_entry'],
      },
      cpData: {
        campaign_id: cId,
      },
      eventType: 'click',
    };

    if (success) {
      ratConfig.cvData = {
        entry_complete: 1,
      };
    } else {
      ratConfig.cpData.entry_error = 1;
    }

    if (isRATTest) {
      ratConfig.accountId = '999';
    }

    try {
      RAT.addCustomEvent(ratConfig);
    } catch (e) {
      console.error(e);
    }
  }
};

export const isValidState = state =>
  Object.keys(buttonStateMap)
    .map(key => buttonStateMap[key])
    .indexOf(state) > -1;

// Redirection
export const redirectToUrl = (url, delay) =>
  new Promise(resolve => {
    setTimeout(() => {
      window.location.href = url;
      resolve();
    }, delay);
  });



// WEBPACK FOOTER //
// ./src/util.js